/** Small pure formatters shared by the face components. */
import type { TFunction } from 'i18next'
import type { MatchEvidenceView } from './match-tier'

/**
 * Scores are raw cosine similarities — two decimals, the precision the
 * thresholds are set in (ADR-0040); a third decimal would only look exact.
 */
export function formatScore(score: number | null | undefined): string {
  return typeof score === 'number' ? score.toFixed(2) : '—'
}

/** A quality warning's short name, as the tiles and the face cards show it; an unknown flag keeps
 *  its raw name rather than disappearing. */
export function qualityFlagName(flag: string, t: TFunction): string {
  return t(`identity.faces.flags.${flag}`, { defaultValue: flag })
}

/** Whether a document is a PDF, by its file type or its file name: only a PDF has pages. */
export function isPdf(documentType: string | null | undefined): boolean {
  return (documentType ?? '').toLowerCase().endsWith('pdf')
}

/**
 * The page to name for a face: a PDF's faces sit on its pages, stored from 0 and named from 1;
 * an image or a recording has no page to name, and its stored 0 is none. *documentType* is the
 * document's file type or its file name.
 */
export function facePageNumber(
  page: number | null | undefined,
  documentType: string | null | undefined
): number | null {
  return page != null && isPdf(documentType) ? page + 1 : null
}

/**
 * A face's quality flags in words, joined — *none* when it has none. An unknown
 * flag keeps its raw name rather than disappearing.
 */
export function describeQualityFlags(flags: readonly string[], t: TFunction, none: string): string {
  if (flags.length === 0) return none
  return flags
    .map(flag =>
      t(`cases.investigate.entityResolution.photoMatching.qualityFlags.${flag}`, {
        defaultValue: flag,
      })
    )
    .join(', ')
}

/** A score that may be missing stays missing, so the view can say why. */
function formatOptionalScore(score: number | null | undefined): string | null {
  return typeof score === 'number' ? formatScore(score) : null
}

/**
 * The figures behind the *suggested match* verdict, as every candidate DTO spells
 * them — the ER table's `FaceMatchingCandidate` and the overlay's
 * `DocumentFaceCandidate` name them the same, so one formatter serves both.
 */
export interface MatchEvidenceFields {
  suggested_match: boolean
  score_second: number | null
  rival_score: number | null
  reference_count: number
  unmet_conditions: string[]
}

/** The API's verdict on a candidate's label, formatted for the comparison. */
export function matchEvidence(candidate: MatchEvidenceFields): MatchEvidenceView {
  return {
    suggestedMatch: candidate.suggested_match,
    scoreSecond: formatOptionalScore(candidate.score_second),
    rivalScore: formatOptionalScore(candidate.rival_score),
    referenceCount: candidate.reference_count,
    unmetConditions: candidate.unmet_conditions,
  }
}
